import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Calculator, FileText, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail, inr } from "./api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

const Field = ({ label, children }) => (
  <div>
    <label className="mono-label block mb-2" style={{ color: "#94A3B8" }}>{label}</label>
    {children}
  </div>
);

export default function Payroll() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const now = new Date();
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    employee_id: params.get("employee") || "",
    month: now.getMonth() + 1,
    year: now.getFullYear(),
    paid_days: 30,
    present_days: 30,
    lwp: 0, overtime_amount: 0, bonus: 0, incentive: 0, reimbursement: 0, advances: 0, extra_deductions: 0,
  });

  const loadRecords = () => api.get("/hr/payroll").then((r) => setRecords(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/hr/employees").then((r) => setEmployees(r.data)).catch(() => {});
    loadRecords();
  }, []);

  const set = (k, num = false) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: num ? (v === "" ? 0 : parseFloat(v)) : v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_id) { toast.error("Please select an employee"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/hr/payroll", {
        ...form,
        month: parseInt(form.month),
        year: parseInt(form.year),
      });
      toast.success(`Payroll generated · Net ${inr(data.computed.net_salary)}`);
      loadRecords();
      navigate(`/portal/payroll/${data.id}/slip`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "field";
  const selectCls = "field appearance-none cursor-pointer";

  const confirmDelete = async () => {
    try {
      await api.delete(`/hr/payroll/${deleteTarget.id}`);
      toast.success("Salary slip removed");
      setDeleteTarget(null);
      loadRecords();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="payroll-page">
      <span className="mono-label" style={{ color: "#00F0FF" }}>Payroll Module</span>
      <h1 className="mt-2 mb-6 font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight">Payroll & Salary Slips</h1>

      <div className="bg-[#0C1322] border border-white/10 p-6 mb-8">
        <div className="flex items-center gap-2 mb-5">
          <Calculator size={18} style={{ color: "#00F0FF" }} />
          <h2 className="font-display font-bold text-lg text-white">Generate Salary</h2>
        </div>
        <form onSubmit={submit} data-testid="payroll-form" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label="Employee *">
            <select required className={selectCls} value={form.employee_id} onChange={set("employee_id")} data-testid="payroll-employee">
              <option value="">Select employee</option>
              {employees.map((e) => <option key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.personal?.full_name}</option>)}
            </select>
          </Field>
          <Field label="Month">
            <select className={selectCls} value={form.month} onChange={set("month")} data-testid="payroll-month">
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </Field>
          <Field label="Year"><input type="number" className={inputCls} value={form.year} onChange={set("year", true)} /></Field>
          <Field label="Paid Days"><input type="number" step="0.5" className={inputCls} value={form.paid_days} onChange={set("paid_days", true)} data-testid="payroll-paid-days" /></Field>
          <Field label="Present Days"><input type="number" step="0.5" className={inputCls} value={form.present_days} onChange={set("present_days", true)} /></Field>
          <Field label="Leave Without Pay (days)"><input type="number" step="0.5" className={inputCls} value={form.lwp} onChange={set("lwp", true)} data-testid="payroll-lwp" /></Field>
          <Field label="Overtime (₹)"><input type="number" className={inputCls} value={form.overtime_amount} onChange={set("overtime_amount", true)} /></Field>
          <Field label="Bonus (₹)"><input type="number" className={inputCls} value={form.bonus} onChange={set("bonus", true)} /></Field>
          <Field label="Incentive (₹)"><input type="number" className={inputCls} value={form.incentive} onChange={set("incentive", true)} /></Field>
          <Field label="Reimbursement (₹)"><input type="number" className={inputCls} value={form.reimbursement} onChange={set("reimbursement", true)} /></Field>
          <Field label="Advances (₹)"><input type="number" className={inputCls} value={form.advances} onChange={set("advances", true)} /></Field>
          <Field label="Extra Deductions (₹)"><input type="number" className={inputCls} value={form.extra_deductions} onChange={set("extra_deductions", true)} /></Field>
          <div className="sm:col-span-2 lg:col-span-4">
            <button type="submit" disabled={saving} data-testid="generate-slip-button"
              className="group inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#00F0FF] text-white hover:text-[#060A14] px-7 py-3.5 mono-label transition-colors duration-300 disabled:opacity-60">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <><Calculator size={16} /> Generate Salary Slip</>}
            </button>
          </div>
        </form>
      </div>

      <h2 className="font-display font-bold text-lg text-white mb-4">Generated Slips</h2>
      <div className="bg-[#0C1322] border border-white/10 overflow-x-auto">
        {records.length === 0 ? (
          <div className="p-10 text-center text-sm" style={{ color: "#64748B" }} data-testid="payroll-empty">No salary slips generated yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="payroll-table">
            <thead>
              <tr className="border-b border-white/10 text-left" style={{ color: "#64748B" }}>
                <th className="p-4 mono-label font-normal">Employee</th>
                <th className="p-4 mono-label font-normal">Period</th>
                <th className="p-4 mono-label font-normal hidden md:table-cell">Gross</th>
                <th className="p-4 mono-label font-normal hidden md:table-cell">Deductions</th>
                <th className="p-4 mono-label font-normal">Net Pay</th>
                <th className="p-4 mono-label font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-[#131C31] transition-colors" data-testid={`payroll-row-${r.id}`}>
                  <td className="p-4 text-white">{r.employee_snapshot?.full_name}<span className="block font-mono-accent text-xs" style={{ color: "#64748B" }}>{r.employee_id}</span></td>
                  <td className="p-4 text-slate-300">{MONTHS[r.month - 1]} {r.year}</td>
                  <td className="p-4 text-slate-300 hidden md:table-cell">{inr(r.computed?.gross_earnings)}</td>
                  <td className="p-4 text-slate-300 hidden md:table-cell">{inr(r.computed?.total_deductions)}</td>
                  <td className="p-4 font-bold" style={{ color: "#00F0FF" }}>{inr(r.computed?.net_salary)}</td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => navigate(`/portal/payroll/${r.id}/slip`)} data-testid={`view-slip-${r.id}`}
                        className="inline-flex items-center gap-1 text-cyan hover:underline mono-label" style={{ color: "#00F0FF" }}>
                        <FileText size={14} /> View
                      </button>
                      <button onClick={() => setDeleteTarget(r)} data-testid={`delete-slip-${r.id}`}
                        className="p-1.5 text-slate-400 hover:text-red-400 transition-colors" title="Remove slip">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#0C1322] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove salary slip?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: "#94A3B8" }}>
              This will permanently delete the {deleteTarget ? `${MONTHS[deleteTarget.month - 1]} ${deleteTarget.year}` : ""} slip for {deleteTarget?.employee_snapshot?.full_name}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-slip"
              className="bg-red-500 hover:bg-red-600 text-white">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
